// Abnahme vor Ort: der Mieter oder Eigentümer quittiert die erledigte Arbeit
// mit Namen und Unterschrift auf dem Telefon.
//
// Bewusst KEIN Dokument und keine E-Signatur — hier wird eine Leistung bestätigt,
// kein Vertrag geschlossen. Die Unterschrift landet als PNG im Ticket-Anhang-Speicher
// (Pfad beginnt mit der Ticket-ID, damit die vorhandenen Zugriffsregeln greifen).
import React, { useEffect, useRef, useState } from 'react';
import { Check, Eraser, Loader2, PenLine, X } from 'lucide-react';
import { Check as PhCheck, CircleNotch as PhCircleNotch, Eraser as PhEraser, Pen as PhPen } from '@phosphor-icons/react';
import { supabase } from '../../lib/supabase/client';

interface Props {
  ticketId: string;
  meineId: string;
  /** Hausmeister-App: Portal-Stil, nur der Inhalt (Titel und Abbrechen stellt das umgebende Widget) */
  dunkel?: boolean;
  onFertig: () => void;
  onAbbruch: () => void;
  onFehler: (s: string) => void;
}

const AbnahmeUnterschrift: React.FC<Props> = ({ ticketId, meineId, dunkel, onFertig, onAbbruch, onFehler }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [name, setName] = useState('');
  const [funktion, setFunktion] = useState<'mieter' | 'eigentuemer' | 'sonstige'>('mieter');
  const [bemerkung, setBemerkung] = useState('');
  const [gezeichnet, setGezeichnet] = useState(false);
  const [speichert, setSpeichert] = useState(false);

  // Zeichenfläche einrichten — Auflösung an das Gerät anpassen, sonst wird die
  // Unterschrift auf Telefonen unscharf.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const breite = c.clientWidth;
    const hoehe = c.clientHeight;
    c.width = breite * dpr;
    c.height = hoehe * dpr;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, breite, hoehe);
  }, []);

  const punkt = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    canvasRef.current!.setPointerCapture(e.pointerId);
    const p = punkt(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    setGezeichnet(true);
  };

  const zeichnen = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.buttons === 0) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const p = punkt(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const leeren = () => {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.clientWidth, c.clientHeight);
    setGezeichnet(false);
  };

  const speichern = async () => {
    if (!name.trim()) { onFehler('Bitte den Namen eintragen.'); return; }
    if (!gezeichnet) { onFehler('Bitte unterschreiben.'); return; }
    const c = canvasRef.current;
    if (!c) return;
    setSpeichert(true);
    const blob: Blob | null = await new Promise((res) => c.toBlob((b) => res(b), 'image/png'));
    if (!blob) { setSpeichert(false); onFehler('Unterschrift konnte nicht erzeugt werden.'); return; }

    const pfad = `${ticketId}/abnahme_${Date.now()}.png`;
    const { error: upErr } = await supabase.storage.from('ticket-anhaenge').upload(pfad, blob, { contentType: 'image/png' });
    if (upErr) { setSpeichert(false); onFehler(`Speichern fehlgeschlagen: ${upErr.message}`); return; }

    const { error } = await supabase.from('ticket_abnahmen').insert({
      ticket_id: ticketId, erfasst_von: meineId, name: name.trim(), funktion,
      bemerkung: bemerkung.trim() || null, unterschrift_path: pfad,
    });
    if (error) { setSpeichert(false); onFehler(error.message); return; }

    // Zusätzlich als Anhang führen, damit die Unterschrift im Ticket auftaucht.
    await supabase.from('ticket_anhaenge').insert({
      ticket_id: ticketId, dateiname: `Abnahme ${name.trim()}.png`, dateipfad: pfad,
      dateityp: 'image/png', dateigroesse: blob.size, hochgeladen_von: meineId,
    });

    setSpeichert(false);
    onFertig();
  };

  // Hausmeister-App (Portal-Stil): nur der Inhalt – Titel „Abnahme vor Ort“ und Abbrechen
  // stellt das umgebende Glas-Widget. Logik oben ist für beide Varianten dieselbe.
  if (dunkel) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input className="p-feld" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name des Unterzeichners"
          aria-label="Name des Unterzeichners" style={{ height: 46, padding: '0 12px' }} />
        <div role="radiogroup" aria-label="Funktion" style={{ display: 'flex', gap: 4, padding: 4, borderRadius: 16, background: 'var(--p-s28)' }}>
          {([['mieter', 'Mieter'], ['eigentuemer', 'Eigentümer'], ['sonstige', 'Sonstige']] as const).map(([k, l]) => (
            <button
              key={k} type="button" role="radio" aria-checked={funktion === k} onClick={() => setFunktion(k)}
              style={{
                flex: 1, height: 38, borderRadius: 12, border: 'none', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                background: funktion === k ? 'var(--p-gold-fill)' : 'transparent',
                color: funktion === k ? 'var(--p-gold-ink)' : 'var(--p-w60)',
              }}
            >
              {l}
            </button>
          ))}
        </div>
        <input className="p-feld" value={bemerkung} onChange={(e) => setBemerkung(e.target.value)} placeholder="Bemerkung (optional)"
          aria-label="Bemerkung (optional)" style={{ height: 46, padding: '0 12px' }} />
        <div style={{ position: 'relative', height: 170, borderRadius: 16, overflow: 'hidden', background: 'var(--p-unterschrift)', boxShadow: 'inset 0 0 0 1px var(--p-unterschrift-linie)' }}>
          <canvas
            ref={canvasRef}
            onPointerDown={start}
            onPointerMove={zeichnen}
            aria-label="Unterschriftsfeld"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', touchAction: 'none', cursor: 'crosshair' }}
          />
          <div aria-hidden style={{ position: 'absolute', left: 18, right: 18, bottom: 34, borderBottom: '1.5px solid var(--p-unterschrift-linie)', pointerEvents: 'none' }} />
          {!gezeichnet && (
            <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, pointerEvents: 'none', color: 'var(--p-unterschrift-hinweis)', fontSize: 14, fontWeight: 600 }}>
              <PhPen size={16} /> Hier unterschreiben
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={leeren} className="p-knopf" data-tip="Unterschrift löschen und neu zeichnen" style={{ color: 'var(--p-w80)' }}>
            <PhEraser size={16} /> Neu
          </button>
          <button type="button" onClick={() => void speichern()} disabled={speichert} className="p-knopf p-knopf-gold" style={{ flex: 1 }}>
            {speichert ? <PhCircleNotch size={16} weight="bold" className="animate-spin" /> : <PhCheck size={16} weight="bold" />} Abnahme bestätigen
          </button>
        </div>
      </div>
    );
  }

  const feld = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-[14px] text-gray-900';

  return (
    <div className={`space-y-2 rounded-2xl border p-3 ${dunkel ? 'border-white/10 bg-white/[0.04]' : 'border-gray-100 bg-white'}`}>
      <div className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest ${dunkel ? 'text-white/35' : 'text-gray-400'}`}>
        <PenLine size={12} /> Abnahme vor Ort
        <button type="button" onClick={onAbbruch} className="ml-auto opacity-60 hover:opacity-100"><X size={14} /></button>
      </div>

      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name des Unterzeichners" className={feld} />

      <div className="flex gap-1.5">
        {([['mieter', 'Mieter'], ['eigentuemer', 'Eigentümer'], ['sonstige', 'Sonstige']] as const).map(([k, l]) => (
          <button
            key={k}
            type="button"
            onClick={() => setFunktion(k)}
            className={`flex-1 rounded-lg px-2 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${
              funktion === k
                ? 'bg-[#c4a77d] text-[#1a1d24]'
                : dunkel ? 'border border-white/15 text-white/60' : 'border border-gray-200 text-gray-500'
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <input value={bemerkung} onChange={(e) => setBemerkung(e.target.value)} placeholder="Bemerkung (optional)" className={feld} />

      <div className="relative">
        <canvas
          ref={canvasRef}
          onPointerDown={start}
          onPointerMove={zeichnen}
          className="h-40 w-full touch-none rounded-lg border border-dashed border-gray-300 bg-white"
        />
        {!gezeichnet && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[13px] text-gray-300">
            Hier unterschreiben
          </span>
        )}
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={leeren}
          className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-[11px] font-bold uppercase tracking-wider ${
            dunkel ? 'border border-white/15 text-white/60' : 'border border-gray-200 text-gray-500'
          }`}
        >
          <Eraser size={13} /> Neu
        </button>
        <button
          type="button"
          onClick={() => void speichern()}
          disabled={speichert}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#c4a77d] px-4 py-2 text-[11px] font-black uppercase tracking-widest text-[#1a1d24] disabled:opacity-50"
        >
          {speichert ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />} Abnahme bestätigen
        </button>
      </div>
    </div>
  );
};

export default AbnahmeUnterschrift;
